import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import SecurityQuestionsEditor from '../components/SecurityQuestionsEditor';

const Auth = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    age: '',
    sex: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    checkin_interval_hours: 168,
    medical_conditions: '',
    health_issues: '',
    sms_consent: false,
    security_q1: '',
    security_a1: '',
    security_q2: '',
    security_a2: '',
    security_q3: '',
    security_a3: ''
  });
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAUserId, setTwoFAUserId] = useState('');
  const [phoneLast4, setPhoneLast4] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setTwoFAUserId(response.data.user_id);
        setPhoneLast4(response.data.phone_last4);
        setShow2FA(true);
        toast.info('Verification code sent to your phone');
        return;
      }

      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        user_id: twoFAUserId,
        code: twoFACode
      });
      toast.success('Verified! Welcome back!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Emergent-managed Google Sign-In callback handler.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;
    const sessionId = new URLSearchParams(hash.slice(1)).get('session_id');
    if (!sessionId) return;
    setGoogleProcessing(true);
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        window.history.replaceState({}, '', '/auth');
        onLogin(res.data.access_token, res.data.user);
        toast.success(`Welcome${res.data.user?.name ? ', ' + res.data.user.name : ''}!`);
        navigate(res.data.user?.role === 'admin' ? '/admin' : '/dashboard');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Google sign-in failed');
        window.history.replaceState({}, '', '/auth');
        setGoogleProcessing(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="auth-container" data-testid="auth-page">
      <div className="auth-box">
        {googleProcessing && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.9)', display: 'grid', placeItems: 'center', zIndex: 10, borderRadius: 'inherit' }} data-testid="google-processing">
            <div style={{ textAlign: 'center' }}>
              <i className="fa-brands fa-google" style={{ fontSize: '2rem', color: '#4285F4' }} />
              <p style={{ marginTop: '0.75rem', color: '#2c5364', fontWeight: 600 }}>Signing you in with Google…</p>
            </div>
          </div>
        )}
        {/* 2FA Verification Screen */}
        {show2FA ? (
          <div data-testid="2fa-section">
            <h1 className="auth-title">Verify Your Identity</h1>
            <p className="auth-subtitle">Enter the 6-digit code sent to ****{phoneLast4}</p>
            <form onSubmit={handle2FASubmit}>
              <div className="form-group">
                <label htmlFor="2fa-code">Verification Code</label>
                <input
                  id="2fa-code"
                  type="text"
                  maxLength={6}
                  value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px', fontWeight: '700' }}
                  data-testid="2fa-code-input"
                />
              </div>
              <button type="submit" className="submit-button" disabled={loading || twoFACode.length !== 6} data-testid="2fa-submit-btn">
                {loading ? 'Verifying...' : 'Verify & Log In'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button type="button" onClick={() => { setShow2FA(false); setTwoFACode(''); }}
                  style={{ background: 'none', border: 'none', color: '#2c5364', cursor: 'pointer', textDecoration: 'underline' }}>
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        ) : (
        <>
        <h1 className="auth-title" data-testid="auth-title">{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
        <p className="auth-subtitle">Join our caregiver wellness community</p>

        <div className="auth-tabs">
          <button 
            className={`tab-button ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
            data-testid="login-tab"
          >
            Login
          </button>
          <button 
            className={`tab-button ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
            data-testid="register-tab"
          >
            Register
          </button>
        </div>

        {/* Continue with Google (Emergent-managed) */}
        <button
          type="button"
          onClick={startGoogleSignIn}
          data-testid="google-signin-btn"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '0.75rem 1rem',
            marginTop: '0.5rem',
            marginBottom: '1rem',
            background: '#fff',
            color: '#3c4043',
            border: '1px solid #dadce0',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'box-shadow 0.15s'
          }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(60,64,67,0.3)'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
          <i className="fa-brands fa-google" style={{ color: '#4285F4', fontSize: '1.1rem' }} />
          Continue with Google
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0 1rem', color: '#94a3b8', fontSize: '0.8rem' }}>
          <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          or {isLogin ? 'sign in' : 'register'} with email
          <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
        </div>

        <form onSubmit={handleSubmit} data-testid="auth-form">
          {!isLogin && (
            <>
              {/* Personal Information */}
              <h3 style={{ color: '#2c5364', marginBottom: '0.75rem', marginTop: '0.5rem', fontSize: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                <i className="fa-solid fa-user" style={{ marginRight: '6px' }}></i> Personal Information
              </h3>
              
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input id="name" type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="name-input" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label htmlFor="age">Age *</label>
                  <input id="age" type="number" name="age" value={formData.age} onChange={handleChange} required min="18" max="120" data-testid="age-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="sex">Sex / Gender *</label>
                  <select id="sex" name="sex" value={formData.sex} onChange={handleChange} required data-testid="sex-select">
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-Binary</option>
                    <option value="prefer-not-to-say">Prefer Not to Say</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+1234567890" data-testid="phone-input" />
              </div>

              {/* Address */}
              <h3 style={{ color: '#2c5364', marginBottom: '0.75rem', marginTop: '1rem', fontSize: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                <i className="fa-solid fa-location-dot" style={{ marginRight: '6px' }}></i> Address
              </h3>

              <div className="form-group">
                <label htmlFor="address">Street Address *</label>
                <input id="address" type="text" name="address" value={formData.address} onChange={handleChange} required placeholder="123 Main Street" data-testid="address-input" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input id="city" type="text" name="city" value={formData.city} onChange={handleChange} required data-testid="city-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <input id="state" type="text" name="state" value={formData.state} onChange={handleChange} required placeholder="TX" maxLength="2" data-testid="state-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="zip_code">ZIP *</label>
                  <input id="zip_code" type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} required placeholder="77001" data-testid="zip-input" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="county">County</label>
                <input id="county" type="text" name="county" value={formData.county} onChange={handleChange} placeholder="County (for local resources)" data-testid="county-input" />
              </div>

              {/* Emergency Contact */}
              <h3 style={{ color: '#2c5364', marginBottom: '0.75rem', marginTop: '1rem', fontSize: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                <i className="fa-solid fa-phone-volume" style={{ marginRight: '6px' }}></i> Emergency Contact
              </h3>

              <div className="form-group">
                <label htmlFor="emergency_contact_name">Contact Name *</label>
                <input id="emergency_contact_name" type="text" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} required placeholder="Full name" data-testid="ec-name-input" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label htmlFor="emergency_contact_phone">Contact Phone *</label>
                  <input id="emergency_contact_phone" type="tel" name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handleChange} required placeholder="+1234567890" data-testid="ec-phone-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="emergency_contact_relationship">Relationship *</label>
                  <select id="emergency_contact_relationship" name="emergency_contact_relationship" value={formData.emergency_contact_relationship} onChange={handleChange} required data-testid="ec-relationship-select">
                    <option value="">Select...</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="neighbor">Neighbor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Health & Check-in */}
              <h3 style={{ color: '#2c5364', marginBottom: '0.75rem', marginTop: '1rem', fontSize: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                <i className="fa-solid fa-heart-pulse" style={{ marginRight: '6px' }}></i> Health & Check-in
              </h3>

              <div className="form-group">
                <label htmlFor="medical_conditions">Medical Conditions</label>
                <input id="medical_conditions" type="text" name="medical_conditions" value={formData.medical_conditions} onChange={handleChange} placeholder="Any relevant conditions" data-testid="medical-input" />
              </div>

              <div className="form-group">
                <label htmlFor="checkin_interval">Check-in Frequency</label>
                <select id="checkin_interval" name="checkin_interval_hours" value={formData.checkin_interval_hours} onChange={handleChange} data-testid="interval-select">
                  <option value="24">Every 24 hours</option>
                  <option value="48">Every 2 days</option>
                  <option value="72">Every 3 days</option>
                  <option value="168">Weekly</option>
                  <option value="336">Every 2 weeks</option>
                </select>
              </div>

              {/* SMS Consent Section - Required for Twilio Compliance */}
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1.25rem', 
                background: 'linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%)', 
                borderRadius: '12px',
                border: '2px solid #2c5364'
              }}>
                <h4 style={{ 
                  margin: '0 0 1rem 0', 
                  color: '#2c5364',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📱 SMS Notifications Consent
                </h4>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input
                    type="checkbox"
                    id="sms_consent"
                    name="sms_consent"
                    checked={formData.sms_consent}
                    onChange={(e) => setFormData({
                      ...formData,
                      sms_consent: e.target.checked
                    })}
                    style={{ 
                      marginTop: '4px', 
                      width: '22px', 
                      height: '22px', 
                      cursor: 'pointer',
                      accentColor: '#2c5364'
                    }}
                    data-testid="sms-consent-checkbox"
                  />
                  <label htmlFor="sms_consent" style={{ cursor: 'pointer', lineHeight: '1.6', color: '#0c5460' }}>
                    <strong>I agree to receive SMS text messages</strong> from CareGuard at the phone number provided above. 
                    Messages include check-in reminders, missed check-in alerts, and emergency notifications. 
                    Message frequency varies based on your check-in schedule (typically 2-10 messages/week). 
                    <strong> Message and data rates may apply.</strong>
                    <br/><br/>
                    Reply STOP to opt out at any time. Reply HELP for assistance.
                    <br/>
                    <a 
                      href="/sms-terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#2c5364', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                      data-testid="sms-terms-link"
                    >
                      View full SMS Terms & Conditions
                    </a>
                  </label>
                </div>
                {formData.phone && !formData.sms_consent && (
                  <p style={{ 
                    margin: '1rem 0 0', 
                    padding: '0.75rem', 
                    background: '#fff3cd', 
                    borderRadius: '6px',
                    color: '#856404',
                    fontSize: '0.9rem'
                  }}>
                    ⚠️ You provided a phone number but have not consented to SMS. 
                    Without consent, you will not receive check-in reminders or emergency alerts via text message.
                  </p>
                )}
              </div>

              {/* Security Questions */}
              <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '1.25rem', marginTop: '1rem' }}>
                <h3 style={{ color: '#2c5364', marginBottom: '0.75rem', fontSize: '1rem' }}>
                  <i className="fa-solid fa-shield-halved" style={{ marginRight: '6px' }}></i>
                  Security Questions
                </h3>
                <p style={{ color: '#6b8899', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  These will be used to verify your identity when recovering your password.
                </p>
                <SecurityQuestionsEditor
                  values={formData}
                  onChange={(name, value) => setFormData((prev) => ({ ...prev, [name]: value }))}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="email-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              data-testid="password-input"
            />
            {isLogin && (
              <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                <Link 
                  to="/forgot-password" 
                  style={{ 
                    color: '#2c5364', 
                    fontSize: '0.9rem',
                    textDecoration: 'none'
                  }}
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className="submit-button" 
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
          </button>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default Auth;